import { enumCreate } from "../utility/enum";
import { IAggregateStoreResponse } from "./aggregateStore";
import { C8AttributeId, C8Node, C8NodeType } from "./node";
import { ISelectOption } from "./selectOption";
import { C8AttributeValue, C8FieldType, C8FieldValue, cnl } from "./value";
import { IValueConstraints } from "./valueConstraints";

// reference: https://centric8.atlassian.net/wiki/spaces/DEV/pages/3720380503/C8+Forms+Architecture#Form-State

export const FORM_ACTION_COPY = "COPY";
export const FORM_ACTION_EDIT = "EDIT";
export const FORM_ACTION_NEW = "NEW";

export const REMEMBER_LAST_VALUE_OPTIONS_ID = "RememberLastValueOptions";
export const REMEMBER_LAST_VALUE_OPTIONS_NONE = enumCreate(REMEMBER_LAST_VALUE_OPTIONS_ID, "None");
export const REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING = enumCreate(REMEMBER_LAST_VALUE_OPTIONS_ID, "WhileCreating");
export const REMEMBER_LAST_VALUE_OPTIONS_ALWAYS = enumCreate(REMEMBER_LAST_VALUE_OPTIONS_ID, "Always");
export type RememberLastValueOptions =
	| typeof REMEMBER_LAST_VALUE_OPTIONS_NONE
	| typeof REMEMBER_LAST_VALUE_OPTIONS_WHILE_CREATING
	| typeof REMEMBER_LAST_VALUE_OPTIONS_ALWAYS;

export const FIELD_PERMISSIONS_OPTIONS_ID = "FieldPermissionsOptions";
export const FIELD_PERMISSIONS_OPTIONS_EDITABLE_ANYWHERE = enumCreate(FIELD_PERMISSIONS_OPTIONS_ID, "EditableAnywhere");
export const FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY = enumCreate(FIELD_PERMISSIONS_OPTIONS_ID, "EditableFormOnly");
export const FIELD_PERMISSIONS_OPTIONS_EDITABLE_CREATE_ONLY = enumCreate(FIELD_PERMISSIONS_OPTIONS_ID, "EditableCreateOnly");
export const FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY = enumCreate(FIELD_PERMISSIONS_OPTIONS_ID, "EditableReadOnly");
export type FieldPermissionsOptions =
	| typeof FIELD_PERMISSIONS_OPTIONS_EDITABLE_ANYWHERE
	| typeof FIELD_PERMISSIONS_OPTIONS_EDITABLE_FORM_ONLY
	| typeof FIELD_PERMISSIONS_OPTIONS_EDITABLE_CREATE_ONLY
	| typeof FIELD_PERMISSIONS_OPTIONS_EDITABLE_READ_ONLY;

export type FormActionType = "COPY" | "EDIT" | "NEW";

export type FormDisabledFields = Set<string>;
export type FormFieldInitialValueMap = Record<string, C8AttributeValue>;

export type FormFieldKeyMap = Record<string, IFormField>;

export interface IFormInitialState {
	/** holding the id of the disabled fields*/
	disabledFields?: FormDisabledFields;

	/** initial value map, holding the initial value of the fields */
	initialValueMap?: FormFieldInitialValueMap;
}

export interface IFormFieldTransientBase {
	/** indicates if field is currently disabled */
	readonly disabled?: boolean;

	/** indicates if field is invalid */
	readonly invalid?: boolean;

	/** message to display under field (could be info message or validation error message) */
	readonly message?: string;

	/** indicates if field is required and cannot have a default value (required status could be dependent on other fields) */
	// FIX: Add option to indicate if it is only required if not empty (such as for Copy Templates)
	required?: boolean;

	/** current value of field */
	value: C8FieldValue;
}

/** object containing properties that can change while using the form */
export interface IFormFieldTransient extends IFormFieldTransientBase {
	/** response from aggregate store query */
	aggregateResponse?: IAggregateStoreResponse;

	/** constraints to apply to component */
	readonly constraints?: IValueConstraints;

	/** value for displaying ref related attributes */
	readonly displayValue?: Object;

	/** indicates if field is editable anywhere */
	fieldPermission: FieldPermissionsOptions;

	/** indicates if user has disabled the expression */
	expressionDisabled?: boolean;

	/** value evaluated by the expression if any */
	readonly expressionValue?: string;

	/** hide the field */
	readonly hidden?: boolean;

	/** indicate whether or not persist the field value */
	persistValue: RememberLastValueOptions;

	/** indicates if field is forced to be read-only, regardless of other logic */
	readOnly?: boolean;

	/** allowed options if a select component */
	selectOptions?: ISelectOption;
}

export interface IFormFieldBase {
	/** format of field */
	readonly format?: string;

	/** label of field (optional if combined with another field) */
	readonly label?: string;

	/** tooltip displayed while editing */
	// TODO - is this still a tooltip?
	readonly tooltip?: string;
}

/** object describing form field */
export interface IFormField extends IFormFieldBase {
	/** attribute id */
	readonly attributeId?: string;

	/** attribute value expression (not allowed on special fields) */
	readonly attributeExpression?: string;

	/** indicate if needs to be populated if only one result, applies to enum type and ref type(LookupItem specific) */
	readonly autoFill?: boolean;

	/** indicates if field value should carryover when using Save & New or Save & Copy */
	readonly carryover?: boolean;

	/** indicates if field should be combined with previous field on same line */
	// TODO: needed for 7.5?
	readonly combineWithPrevious?: boolean;

	/** create only */
	createOnly?: boolean;

	/** indicates if the field is a custom, non-OOTB attribute */
	readonly customAttribute?: boolean;

	/** destination class name */
	readonly destinationClassName?: C8NodeType;

	/** for enum attributes only - indicates attribute that dictates available enum values */
	// TODO - this could be extended to potentially any field as with Heading Dependency
	readonly dependsOnAttributeId?: string;

	/** indicate if the field is form only */
	formOnly?: boolean;

	/** indicate if the field has aggregate store or not */
	readonly hasAggregateStore: boolean;

	/** unique id */
	readonly id: string;

	/** indicates if field is inactive (should not be displayed) */
	readonly inactive?: boolean;

	/** initial value of field */
	readonly initialValue: any;

	/** notes */
	readonly notes?: string;

	/** path to attribute relative to type of form (not allowed on special fields) */
	readonly path?: string;

	/** reflection attribute owner, type of node to which attribute belongs */
	readonly reflectionAttributeOwner?: C8NodeType;

	/** force user to enter text before querying aggregate store */
	readonly requireText?: boolean;

	/** transient information for field (can change during execution of form) */
	transientInfo: IFormFieldTransient;

	/** section id */
	readonly section?: string;

	/** indicates if special field */
	readonly special?: boolean;

	/** validation expression */
	readonly validationExpression?: string;

	/** type of value */
	readonly valueType: C8FieldType;
};

/** object describing form */
export interface IForm extends IFormInitialState {
	/** relation from context node to node being created */
	contextAttributeId?: C8AttributeId;

	/** url of node where action was invoked - could be creating a node in the context of another, but two nodes have no direct relation (i.e. Sample under a Style) */
	contextUrl?: cnl;

	/** fields in form */
	readonly fields: IFormField[];

	/** list of nodes needed for processing logic/expressions in form, which will minimally include node, parent, context and source nodes (if they exist) */
	nodes: C8Node[];

	/** root node type of form */
	nodeType: C8NodeType;

	/** url of node being created or edited; if being created, nodeUrl is not final (persisted), but node will exist in "nodes" array */
	nodeUrl?: cnl;

	/** relation from parent to node being created */
	parentAttributeId?: C8AttributeId;

	/** key to use when parent will reference form node from a refmap */
	parentAttributeKey?: string;

	/** parent url of node being created or edited */
	parentUrl?: cnl;

	/** XML node operations script to create runtime context for aggregate store */
	readonly reportOperations?: string;

	/** url of node being edited or copied */
	sourceNodeUrl?: cnl;
}
